/* eslint-disable max-lines */
import { Link } from '@tanstack/react-router';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { ExternalLinkIcon } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';

import { TableHeader } from '@/components/DataTable/TableHeader';
import {
  getTranslation,
  type HandledCloudResource,
} from '@/lib/types/resourceDefs';

import { getImpactZone, getManagementStatus, IMPACTS_MANAGED } from './utils';

export type CoverageRow = HandledCloudResource & {
  updates?: string[];
  impactType?: 'upstream' | 'downstream';
};

const columnHelper = createColumnHelper<CoverageRow>();

export const useColumns = (
  from: string | undefined,
  to: string | undefined,
  showImpactType: boolean,
): ColumnDef<CoverageRow>[] => {
  const intl = useIntl();

  return [
    columnHelper.accessor(row => row.service + '.' + row.resourceType, {
      id: 'resourceType',
      header: ({ column }) => (
        <TableHeader
          type="select"
          column={column}
          headersTranslationPath="coverage.overview.table"
          optionsTranslationPath="coverage.emojiResourceTypes"
        />
      ),
      cell: ({ row }) => {
        return getTranslation(row.original, intl.formatMessage, true);
      },
    }),
    columnHelper.accessor('resourceId', {
      header: ({ column }) => (
        <TableHeader
          type="input"
          column={column}
          headersTranslationPath="coverage.overview.table"
        />
      ),
      cell: ({ row }) => {
        const { resourceId, hashedID } = row.original;

        return (
          <Link
            className="underline hover:text-blue-500"
            to="/resource/$resourceId"
            params={{ resourceId: hashedID }}
            search={{ from, to }}
          >
            {resourceId}
          </Link>
        );
      },
    }),
    ...(showImpactType
      ? [
          columnHelper.accessor('impactType', {
            id: 'impactType',
            header: ({ column }) => (
              <TableHeader
                type="select"
                column={column}
                headersTranslationPath="coverage.overview.table"
                optionsTranslationPath="coverage.overview.table.impactType.options"
              />
            ),
            cell: ({ getValue }) => {
              const value = getValue();

              return value !== undefined ? (
                <FormattedMessage
                  id={`coverage.overview.table.impactType.options.${value}`}
                />
              ) : (
                <FormattedMessage id="global.noValue" />
              );
            },
          }),
        ]
      : []),
    columnHelper.accessor(({ awsURL }) => awsURL, {
      header: () => (
        <FormattedMessage id="coverage.overview.table.awsUrl.header" />
      ),
      id: 'awsUrl',
      cell: row =>
        row.getValue() !== '' ? (
          <a
            href={decodeURIComponent(row.getValue())}
            target="_blank"
            rel="noreferrer"
            className="underline hover:text-blue-500"
          >
            <FormattedMessage id="search.table.awsUrl.cell" />
            &nbsp;
            <ExternalLinkIcon className="h-4 inline" />
          </a>
        ) : (
          <FormattedMessage id="global.noValue" />
        ),
    }),
    columnHelper.accessor(getManagementStatus, {
      id: 'codeUrl',
      header: ({ column }) => (
        <TableHeader
          type="select"
          column={column}
          headersTranslationPath="coverage.overview.table"
          optionsTranslationPath="coverage.overview.table.codeUrl.headerOptions"
        />
      ),
      cell: ({ row }) =>
        row.original.managed ? (
          <div>
            {row.original.codeGithubURL !== undefined ? (
              <a
                href={row.original.codeGithubURL}
                target="_blank"
                rel="noreferrer"
                className="underline inline-block hover:text-blue-500"
              >
                <FormattedMessage id="coverage.overview.table.codeUrl.resource" />
                &nbsp;
                <ExternalLinkIcon className="h-4 inline" />
              </a>
            ) : (
              <></>
            )}
            {row.original.moduleGithubURL !== undefined ? (
              <a
                href={row.original.moduleGithubURL}
                target="_blank"
                rel="noreferrer"
                className="underline inline-block hover:text-blue-500"
              >
                <FormattedMessage id="coverage.overview.table.codeUrl.module" />
                &nbsp;
                <ExternalLinkIcon className="h-4 inline" />
              </a>
            ) : (
              <></>
            )}
          </div>
        ) : (
          <FormattedMessage id="coverage.overview.table.codeUrl.unmanaged" />
        ),
    }),
    columnHelper.accessor(getImpactZone, {
      id: 'impactZone',
      header: ({ column }) => (
        <TableHeader
          type="select"
          column={column}
          headersTranslationPath="coverage.overview.table"
          optionsTranslationPath="coverage.overview.table.impactZone.headerOptions"
        />
      ),
      cell: row => {
        const value = row.getValue();

        return value !== undefined ? (
          <FormattedMessage
            id={`coverage.overview.table.impactZone.options.${value}`}
            values={{
              count:
                value === IMPACTS_MANAGED
                  ? row.row.original.impacts.filter(({ managed }) => managed)
                      .length
                  : row.row.original.impactedBy.filter(
                      ({ managed }) => !managed,
                    ).length,
            }}
          />
        ) : (
          <FormattedMessage id="global.noValue" />
        );
      },
    }),
    columnHelper.accessor('updatedAt', {
      header: ({ column }) => (
        <TableHeader
          type="sort-only"
          column={column}
          headersTranslationPath="coverage.overview.table"
        />
      ),
      cell: ({ row }) => {
        return row.original.updatedAt;
      },
    }),
    ...(from !== undefined || to !== undefined
      ? [
          columnHelper.accessor(({ updates }) => updates?.length ?? 0, {
            id: 'updates',
            header: ({ column }) => (
              <TableHeader
                type="sort-only"
                column={column}
                headersTranslationPath="coverage.overview.table"
              />
            ),
            cell: ({ getValue }) => getValue(),
          }),
        ]
      : []),
  ] as ColumnDef<CoverageRow>[];
};
